// components/AgregarPagoModal.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  InputAdornment,
  Chip,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { SubirComprobanteTransferencia } from './SubirComprobanteTransferencia';

export const AgregarPagoModal = ({ 
  open, 
  onClose, 
  mediosPago,
  pedidoId,
  uuid, // Recibir el uuid_pedido desde el componente padre
  onPagoAgregado,
  diferenciaFaltante   
}) => {
  const [medioPagoId, setMedioPagoId] = useState('');
  const [importe, setImporte] = useState('');
  const [detalle, setDetalle] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Campo para tarjeta (formato completo: 1234-123)
  const [cuponLote, setCuponLote] = useState('');
  
  // Estado para el modal de comprobante de transferencia
  const [openSubirComprobante, setOpenSubirComprobante] = useState(false);
  const [comprobanteData, setComprobanteData] = useState(null);

  const medioPagoSeleccionado = mediosPago.find(mp => mp.id === parseInt(medioPagoId));

  // Determinar si el medio de pago es efectivo, tarjeta o transferencia
  const esEfectivo = medioPagoSeleccionado?.nombre?.toLowerCase() === 'efectivo';
  const esTarjeta = medioPagoSeleccionado?.nombre?.toLowerCase().includes('tarjeta') || 
                     medioPagoSeleccionado?.nombre?.toLowerCase().includes('crédito') ||
                     medioPagoSeleccionado?.nombre?.toLowerCase().includes('débito');
  const esTransferencia = medioPagoSeleccionado?.nombre?.toLowerCase().includes('transferencia') ||
                          medioPagoSeleccionado?.nombre?.toLowerCase().includes('transfer');

  // Generar detalle según el tipo de pago
  const generarDetalle = () => {
    if (esTarjeta) {
      return cuponLote || '';
    }
    
    if (esTransferencia && comprobanteData) {
      return ``;
    }
    
    return detalle;
  };

  // Manejar cuando se sube el comprobante
  const handleComprobanteSubido = (data) => {
    if (data.success) {
      setComprobanteData(data);
      setError('');
    } else {
      setError('Error al subir el comprobante: ' + data.error);
    }
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!medioPagoId) {
      setError('Seleccione un medio de pago');
      return;
    }

    const importeNumerico = parseFloat(importe.replace(',', '.'));
    if (isNaN(importeNumerico) || importeNumerico <= 0) {
      setError('Ingrese un importe válido mayor a 0');
      return;
    }

    // Validaciones específicas según tipo de pago
    if (esTarjeta) {
      if (!cuponLote) {
        setError('Ingrese el número de cupón y lote en formato 1234-123');
        return;
      }
      // Validar formato: 4 dígitos - 3 dígitos
      const regex = /^\d{4}-\d{3}$/;
      if (!regex.test(cuponLote)) {
        setError('Formato incorrecto. Debe ser: 1234-123 (4 dígitos, guión, 3 dígitos)');
        return;
      }
    }

    if (esTransferencia && !comprobanteData) {
      setError('Debe adjuntar el comprobante de transferencia');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const detalleFinal = generarDetalle();
      
      const nuevoPago = {
        pedido: pedidoId,
        medio_pago: parseInt(medioPagoId),
        importe: importeNumerico,
        detalle: detalleFinal || detalle || ``,
        usuario: null,
      };

      // Llamar al callback para agregar el pago
      if (onPagoAgregado) {
        await onPagoAgregado(nuevoPago);
      }

      // Limpiar formulario
      setMedioPagoId('');
      setImporte('');
      setDetalle('');
      setCuponLote('');
      setComprobanteData(null);
      onClose();
    } catch (err) {
      setError(err.message || 'Error al agregar el pago');
    } finally {
      setLoading(false);
    }
  };

  const handleImporteChange = (e) => {
    let value = e.target.value;
    value = value.replace(',', '.');
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setImporte(value);
    }
  };

  const handleCuponLoteChange = (e) => {
    let value = e.target.value;
    // Permitir solo números y guión, con formato automático
    value = value.replace(/[^\d-]/g, '');
    
    // Formateo automático: al escribir 4 números agrega el guión
    if (value.length === 4 && !value.includes('-')) {
      value = value + '-';
    }
    
    // Limitar longitud a 8 caracteres (1234-123)
    if (value.length <= 8) {
      setCuponLote(value);
    }
  };

  const formatImporteMostrar = (importe) => {
    if (!importe) return '';
    const num = parseFloat(importe);
    if (isNaN(num)) return '';
    return num.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleCloseModal = () => {
    if (!loading) {
      // Limpiar estados
      setMedioPagoId('');
      setImporte('');
      setDetalle('');
      setCuponLote('');
      setComprobanteData(null);
      setError('');
      onClose();
    }
  };

  // Validar si el formulario es válido para habilitar el botón
const esFormularioValido = () => {
  // Validar medio de pago
  if (!medioPagoId) return false;
  
  // Validar importe
  const importeNumerico = parseFloat(importe.replace(',', '.'));
  if (isNaN(importeNumerico) || importeNumerico <= 0) return false;
  
  // Validaciones específicas por tipo de pago
  if (esTarjeta) {
    if (!cuponLote) return false;
    const regex = /^\d{4}-\d{3}$/;
    if (!regex.test(cuponLote)) return false;
  }
  
  if (esTransferencia) {
    if (!comprobanteData) return false;
  }
  
  return true;
};

  return (
    <>
      <Dialog open={open} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <AddIcon sx={{ mr: 1, color: '#2E86C1' }} />
            <Typography variant="h6">
              Agregar Nuevo Pago
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Mostrar diferencia que falta */}
          {diferenciaFaltante > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Saldo pendiente a cubrir: ${formatImporteMostrar(diferenciaFaltante)}</strong>
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Medio de Pago</InputLabel>
            <Select
              value={medioPagoId}
              onChange={(e) => {
                setMedioPagoId(e.target.value);
                // Limpiar campos específicos al cambiar el medio de pago
                setCuponLote('');
                setComprobanteData(null);
                setDetalle('');
              }}
              label="Medio de Pago"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Seleccione un medio de pago</em>
              </MenuItem>
              {mediosPago
                .filter(mp => mp.id !== 6 && mp.id !== 11 )
                .map((mp) => (
                  <MenuItem key={mp.id} value={mp.id}>
                    {mp.nombre}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Importe"
            value={importe}
            onChange={handleImporteChange}
            placeholder="0.00"
            sx={{ mb: 2 }}
            disabled={loading}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />

          {/* Botón para sugerir el monto faltante */}
          {diferenciaFaltante > 0 && (
            <Button 
              size="small" 
              onClick={() => setImporte(diferenciaFaltante.toString())}
              sx={{ mb: 2 }}
            >
              Usar monto pendiente (${formatImporteMostrar(diferenciaFaltante)})
            </Button>
          )}

          {/* Campo para Tarjeta de Crédito/Débito */}
          {esTarjeta && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
                Datos de la tarjeta
              </Typography>
              <TextField
                fullWidth
                required  
                label="Cupón-Lote"
                value={cuponLote}
                onChange={handleCuponLoteChange}
                placeholder="1234-123"
                sx={{ mb: 1 }}
                disabled={loading}
                helperText="Formato: 1234-123 (4 dígitos, guión, 3 dígitos)"
                inputProps={{ maxLength: 8 }}
              />
              <Typography variant="caption" color="textSecondary">
                Ejemplo: 1234-567
              </Typography>
            </Box>
          )}

          {/* Campo para Transferencia */}
          {esTransferencia && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
                Comprobante de Transferencia
              </Typography>
              
              {!comprobanteData ? (
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setOpenSubirComprobante(true)}
                  disabled={loading}
                  fullWidth
                >
                  Adjuntar comprobante
                </Button>
              ) : (
                <Alert 
                  severity="success" 
                  sx={{ mt: 1 }}
                  action={
                    <IconButton
                      size="small"
                      onClick={() => {
                        setComprobanteData(null);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <Typography variant="body2">
                    <strong>Comprobante adjunto:</strong> {comprobanteData.nombre}
                  </Typography>
                </Alert>
              )}
              
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Formatos permitidos: JPG, PNG, PDF (máx. 5MB)
              </Typography>
            </Box>
          )}

          {/* Campo de detalle manual para otros medios de pago */}
          {!esTarjeta && !esTransferencia && !esEfectivo && (
            <TextField
              fullWidth
              label="Detalle (opcional)"
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
              disabled={loading}
            />
          )}

          {/* Mostrar resumen del detalle que se guardará */}
          {(esTarjeta || (esTransferencia && comprobanteData)) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Detalle a guardar:</strong> {generarDetalle() || 'Completar los campos requeridos'}
              </Typography>
            </Alert>
          )}

          {medioPagoSeleccionado && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Medio seleccionado:</strong> {medioPagoSeleccionado.nombre}
              </Typography>
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
  <Button onClick={handleCloseModal} disabled={loading}>
    Cancelar
  </Button>
  <Button 
    onClick={handleSubmit} 
    variant="contained" 
    color="primary"
    disabled={loading || !esFormularioValido()}  
  >
    {loading ? 'Agregando...' : 'Agregar Pago'}
  </Button>
</DialogActions>
      </Dialog>

      {/* Modal para subir comprobante de transferencia */}
      <SubirComprobanteTransferencia
        open={openSubirComprobante}
        onClose={() => setOpenSubirComprobante(false)}
        uuidPedido={uuid}
        onComprobanteSubido={handleComprobanteSubido}
        medioPagoNombre={medioPagoSeleccionado?.nombre}
      />
    </>
  );
};